import { memo, useRef } from 'react';
import { Info } from 'lucide-react';
import { TagSwatch } from './TagSwatch';
import { money } from '../../utils/nurseryMoney';
import type { SheetSection } from '../../utils/orderSheet';
import type { OrderSheetRow } from '../../types/nursery';

/**
 * The order sheet's grid: every stock item and product under its tree type
 * and crop, with the free stock beside a quantity box. Type a number, press
 * Enter or the down arrow, type the next. Each box saves when it is left.
 */

export type CellState = { state: 'saving' } | { state: 'error'; message: string };

export interface OrderSheetGridProps {
  sections: SheetSection[];
  /** What each box holds right now, by row key. */
  quantities: Record<string, string>;
  /** The saved line's total, by row key. */
  lineTotals: Record<string, number>;
  /** Saved lines with no price, by row key. */
  unpriced: Record<string, boolean>;
  cells: Record<string, CellState>;
  disabled: boolean;
  onType: (key: string, text: string) => void;
  onLeave: (row: OrderSheetRow) => void;
  /** Open what to tell a customer about a row. Offered only on rows with something written. */
  onInfo: (row: OrderSheetRow) => void;
}

interface RowProps {
  row: OrderSheetRow;
  text: string;
  lineTotal: number | undefined;
  noPrice: boolean;
  cell: CellState | undefined;
  disabled: boolean;
  onType: (key: string, text: string) => void;
  onLeave: (row: OrderSheetRow) => void;
  onInfo: (row: OrderSheetRow) => void;
  onMove: (from: HTMLInputElement, step: 1 | -1) => void;
}

const ROW_GRID = 'grid grid-cols-[minmax(0,1fr)_3.5rem_4.5rem] sm:grid-cols-[minmax(0,1fr)_4rem_5rem_5.5rem] gap-x-2 items-center';

const SheetRow = memo(function SheetRow({ row, text, lineTotal, noPrice, cell, disabled, onType, onLeave, onInfo, onMove }: RowProps) {
  const qty = /^\d+$/.test(text.trim()) ? parseInt(text.trim(), 10) : 0;
  const several = row.line_count > 1;
  // Trees already allocated to this order are not in free; they are its own.
  const allocated = row.allocated ?? 0;
  const over = qty - allocated > row.free;
  const releasing = allocated > qty ? allocated - qty : 0;
  const inputId = `qty-${row.key}`;
  return (
    <li className={`px-3 py-1.5 ${qty > 0 ? 'bg-primary-50/60' : ''}`}>
      <div className={ROW_GRID}>
        <div className="min-w-0 flex items-start gap-1">
          <label htmlFor={inputId} className="min-w-0 flex-1 text-sm text-neutral-900 break-words">
            <span className="inline-flex items-baseline gap-1.5 max-w-full">
              {row.product_id != null && <TagSwatch hex={row.tag_colour_hex} name={row.tag_colour_name} className="self-center" />}
              <span className="min-w-0 break-words">{row.name}</span>
            </span>
            {row.rootstock_name && <span className="block text-xs text-neutral-500 break-words">on {row.rootstock_name}</span>}
            {row.product_id != null && <span className="block text-xs text-neutral-500">Product</span>}
          </label>
          {/* Only where something has been written: a button that opens an empty panel teaches people not to press it. */}
          {row.has_info && (
            <button
              type="button"
              tabIndex={-1}
              onClick={() => onInfo(row)}
              aria-label={`What to tell a customer about ${row.name}`}
              title="What to tell a customer"
              className="flex-shrink-0 p-1 -my-0.5 rounded text-neutral-400 hover:text-primary-700 hover:bg-neutral-100"
            >
              <Info className="h-4 w-4" />
            </button>
          )}
        </div>
        <div className="text-right text-sm tabular-nums">
          <span className={row.free > 0 ? 'text-neutral-800' : 'text-neutral-400'}>{row.free}</span>
          {allocated > 0 && (
            <span className="block text-[11px] leading-tight text-indigo-700" title="Trees already allocated to this order">
              {allocated} alloc.
            </span>
          )}
          {row.wanted > 0 && (
            <span className="block text-[11px] leading-tight text-amber-700" title="Trees other confirmed orders want and have not been allocated">
              {row.wanted} wanted
            </span>
          )}
        </div>
        <input
          id={inputId}
          data-qty
          type="text"
          inputMode="numeric"
          autoComplete="off"
          disabled={disabled || several}
          title={several ? 'This order has more than one line for this item. Change it on the order page.' : undefined}
          aria-invalid={cell?.state === 'error'}
          value={text}
          onChange={(e) => onType(row.key, e.target.value)}
          onBlur={() => onLeave(row)}
          onFocus={(e) => e.target.select()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === 'ArrowDown') { e.preventDefault(); onMove(e.currentTarget, 1); }
            else if (e.key === 'ArrowUp') { e.preventDefault(); onMove(e.currentTarget, -1); }
          }}
          className={`w-full border rounded-md px-2 py-1.5 text-sm text-right tabular-nums focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-neutral-100 disabled:text-neutral-500 ${
            cell?.state === 'error' ? 'border-red-500 bg-red-50'
              : cell?.state === 'saving' ? 'border-amber-400'
                : over ? 'border-amber-500' : 'border-neutral-300'
          }`}
        />
        <div className="hidden sm:block text-right text-sm tabular-nums text-neutral-700">
          {qty > 0 && noPrice ? <span className="text-amber-700">No price</span> : qty > 0 && lineTotal != null ? money(lineTotal) : ''}
        </div>
      </div>
      {cell?.state === 'error' && (
        <p className="mt-1 text-xs text-red-600 break-words" role="alert">{cell.message}</p>
      )}
      {cell?.state !== 'error' && qty > 0 && noPrice && (
        <p className="mt-1 text-xs text-amber-700 break-words">
          No price on this customer's price lists. It will not be invoiced until it has one.
        </p>
      )}
      {cell?.state !== 'error' && over && (
        <p className="mt-1 text-xs text-amber-700 break-words">
          {row.free > 0 ? `Only ${row.free} free.` : 'None free.'} The order can still be placed.
        </p>
      )}
      {cell?.state !== 'error' && releasing > 0 && (
        <p className="mt-1 text-xs text-amber-700 break-words">
          {allocated} allocated. Saving sends {releasing} back to free stock.
        </p>
      )}
    </li>
  );
});

export function OrderSheetGrid({ sections, quantities, lineTotals, unpriced, cells, disabled, onType, onLeave, onInfo }: OrderSheetGridProps) {
  const gridRef = useRef<HTMLDivElement>(null);

  // Enter and the arrows walk the boxes in the order they read down the page.
  const move = useRef((from: HTMLInputElement, step: 1 | -1) => {
    const boxes = Array.from(gridRef.current?.querySelectorAll<HTMLInputElement>('input[data-qty]:not(:disabled)') ?? []);
    const next = boxes[boxes.indexOf(from) + step];
    if (next) next.focus();
    else from.blur();
  }).current;

  return (
    <div ref={gridRef} className="space-y-6">
      {sections.map((section) => (
        <section key={section.treeTypeId} aria-label={section.treeTypeName}>
          <h3 className="text-base font-semibold text-neutral-900 mb-2 break-words">{section.treeTypeName}</h3>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 2xl:grid-cols-3 items-start">
            {section.crops.map((group) => (
              <div key={group.crop} className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
                <div className={`${ROW_GRID} px-3 py-2 bg-neutral-100 border-b border-neutral-200`}>
                  <h4 className="min-w-0 text-sm font-semibold text-neutral-800 break-words">{group.crop}</h4>
                  <span className="text-right text-xs font-medium text-neutral-500">Free</span>
                  <span className="text-right text-xs font-medium text-neutral-500">Qty</span>
                  <span className="hidden sm:block text-right text-xs font-medium text-neutral-500">Total</span>
                </div>
                <ul className="divide-y divide-neutral-100">
                  {group.rows.map((row) => (
                    <SheetRow
                      key={row.key}
                      row={row}
                      text={quantities[row.key] ?? ''}
                      lineTotal={lineTotals[row.key]}
                      noPrice={!!unpriced[row.key]}
                      cell={cells[row.key]}
                      disabled={disabled}
                      onType={onType}
                      onLeave={onLeave}
                      onInfo={onInfo}
                      onMove={move}
                    />
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
